import React from 'react';
import _ from 'underscore';

import {FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {FieldFilter, Operator, VALUELESS_OPERATORS} from 'search/SearchConstants';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';

export interface SchemaFieldFilterFormSkeletonProps extends BaseFilterFormProps {
  filterStyle: FilterStyle;
  filter: FieldFilter;
  onChangeFilter: (filter: FieldFilter) => void;
  onChangeSchemaField: (value: SchemaField) => void;
  onRemove?: () => void;
  operators: Array<Operator>;
  schemaFields: Array<SchemaField>;
  selectedSchemaField: SchemaField | null;
  operatorDropdown: JSX.Element;
  valueNodes?: Array<JSX.Element>;
  schemaSelectorNode?: JSX.Element;
  allowBlobLinkFields?: boolean;
}

export function getOptionDatasForSchemaFields(
  schemaFields: Array<SchemaField>,
  allowBlobLinkFields?: boolean
): Array<OptionData> {
  return schemaFields.map((schemaField: SchemaField) => ({
    name: schemaField.displayName,
    value: schemaField,
    disabled: !allowBlobLinkFields && schemaField.fieldType === FIELD_TYPES.BLOB_LINK.type,
    disabledMessage:
      !allowBlobLinkFields && schemaField.fieldType === FIELD_TYPES.BLOB_LINK.type
        ? 'Filter is not supported for this field type'
        : undefined,
  }));
}

const SchemaFieldFilterFormSkeleton: React.SFC<SchemaFieldFilterFormSkeletonProps> = (
  props: SchemaFieldFilterFormSkeletonProps
): JSX.Element => {
  const optionDatas = getOptionDatasForSchemaFields(props.schemaFields, props.allowBlobLinkFields);
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const schemaFieldDropdown = (
    <Dropdown
      menu={
        <SearchableMenu
          scrollable={true}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FIT_CONTENT}
          selectedValue={props.selectedSchemaField}
          optionDatas={optionDatas}
          onSelect={props.onChangeSchemaField}
        />
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        fullWidth={true}
        isDropdown={true}
        disabled={props.schemaFields.length === 0}
        size={props.buttonSize}
        text={props.selectedSchemaField ? props.selectedSchemaField.displayName : 'Select field...'}
        dataTestAttrs={{component: 'SchemaFieldDropdown', element: 'button'}}
      />
    </Dropdown>
  );

  const isValuelessOperator = VALUELESS_OPERATORS.includes(props.filter.operator);
  const nodeGrid = _.compact([
    props.schemaSelectorNode && [props.schemaSelectorNode],
    [schemaFieldDropdown],
    props.selectedSchemaField && [props.operatorDropdown],
    props.selectedSchemaField && isValuelessOperator ? [] : props.valueNodes,
  ]);
  if (props.filterStyle === FilterStyle.PRIMARY) {
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        content={<FilterFormSkeletonNodeLayout nodeGrid={nodeGrid} />}
        dropdownToggleText="Field"
      />
    );
  } else {
    if (!props.onRemove) {
      throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
    }
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={nodeGrid}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove}
      />
    );
  }
};

export default SchemaFieldFilterFormSkeleton;
